import { Alert, Tooltip } from '@mui/material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import React, { useState } from 'react';
import PDFTicket from '../../Tools/PDFTicket';

const PaymentHistoryTable = ({ payments, showActions, applyPayment, message }) => {
  const [sortField, setSortField] = useState('date_payment');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedPayment, setSelectedPayment] = useState(null);

  // Función para formatear dinero
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Función para formatear fecha y hora
  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Ordenar pagos
  const sortedPayments = [...payments].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    if (sortField === 'date_payment' || sortField === 'transfer_date') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Paginación
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPayments = sortedPayments.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedPayments.length / itemsPerPage);

  // Cambiar ordenamiento
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Ver evidencia
  const viewEvidence = (payment) => {
    const base64 = payment.evidence_base64;
  
    if (!base64) return;
  
    const isPDF = base64.startsWith('data:application/pdf');
  
    if (isPDF) {
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <iframe
            src="${base64}"
            frameborder="0"
            style="width:100%; height:100%;"
          ></iframe>
        `);
      } else {
        alert("No se pudo abrir una nueva ventana. Verifica el bloqueador de ventanas emergentes.");
      }
    } else {
      setSelectedPayment(payment);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span style={{ opacity: 0.3, marginLeft: '4px', fontSize: '10px' }}>↕</span>;
    return <span style={{ marginLeft: '4px', fontSize: '10px', color: '#fca5a5' }}>{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const statusStyle = (status) => {
    if (status === 0) return { bg: '#dcfce7', color: '#166534' };
    if (status === 1) return { bg: '#fef9c3', color: '#854d0e' };
    return { bg: '#fee2e2', color: '#991b1b' };
  };

  const iconBtn = (onClick, title, iconClass, hoverBg) => (
    <Tooltip title={title}>
      <button
        onClick={onClick}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: '30px', height: '30px', borderRadius: '6px', border: 'none',
          backgroundColor: 'transparent', cursor: 'pointer', transition: 'background-color 0.12s',
        }}
        onMouseEnter={e => e.currentTarget.style.backgroundColor = hoverBg}
        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
      >
        <i className={iconClass} style={{ fontSize: '14px' }}></i>
      </button>
    </Tooltip>
  );

  const thStyle = {
    backgroundColor: '#111827', color: '#f9fafb', fontWeight: 700,
    fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em',
    padding: '10px 12px', whiteSpace: 'nowrap', cursor: 'pointer',
    borderBottom: '2px solid #dc2626', userSelect: 'none',
  };

  const thNoSort = { ...thStyle, cursor: 'default' };

  const tdStyle = (index) => ({
    padding: '8px 12px',
    fontSize: '0.78rem',
    borderBottom: '1px solid #f1f5f9',
    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc',
  });

  return (
    <div className="bg-white rounded-lg" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0' }}>
      <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid #f1f5f9' }}>
        <h2 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#111827', margin: 0 }}>Historial de Pagos</h2>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>{sortedPayments.length} registros</span>
      </div>

      {message.status && (
        <div className="px-5 pt-3">
          <Alert severity={message.label}>{message.message}</Alert>
        </div>
      )}

      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle} onClick={() => handleSort('invoice')}>
                Folio Pago <SortIcon field="invoice" />
              </th>
              <th style={thStyle} onClick={() => handleSort('amount')}>
                Monto <SortIcon field="amount" />
              </th>
              <th style={thStyle} onClick={() => handleSort('period')}>
                Período <SortIcon field="period" />
              </th>
              <th style={thNoSort}>Método</th>
              <th style={thStyle} onClick={() => handleSort('transfer_date')}>
                F. Transferencia <SortIcon field="transfer_date" />
              </th>
              <th style={thStyle} onClick={() => handleSort('date_payment')}>
                F. Registro <SortIcon field="date_payment" />
              </th>
              <th style={thNoSort}>Folio</th>
              <th style={thNoSort}>A Favor</th>
              <th style={thNoSort}>Estado</th>
              <th style={thNoSort}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {currentPayments.map((payment, index) => {
              const st = statusStyle(payment.status);
              return (
                <tr
                  key={payment.partial_payment_id}
                  style={{ transition: 'background-color 0.1s' }}
                  onMouseEnter={e => e.currentTarget.querySelectorAll('td').forEach(td => td.style.backgroundColor = '#fef2f2')}
                  onMouseLeave={e => e.currentTarget.querySelectorAll('td').forEach(td => td.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f8fafc')}
                >
                  <td style={{ ...tdStyle(index), fontFamily: 'monospace', fontSize: '0.72rem', color: '#374151' }}>
                    {payment.invoice}
                  </td>
                  <td style={{ ...tdStyle(index), fontWeight: 600, color: '#111827' }}>
                    {formatMoney(payment.amount)}
                  </td>
                  <td style={tdStyle(index)}>{payment.period}</td>
                  <td style={tdStyle(index)}>{payment.method_payment}</td>
                  <td style={{ ...tdStyle(index), color: '#6b7280' }}>
                    {payment.transfer_date ? formatDate(payment.transfer_date) : <span style={{ color: '#d1d5db' }}>—</span>}
                  </td>
                  <td style={{ ...tdStyle(index), color: '#6b7280' }}>
                    {formatDateTime(payment.date_payment)}
                  </td>
                  <td style={{ ...tdStyle(index), fontFamily: 'monospace', fontSize: '0.72rem' }}>
                    {payment.folio_transfer || <span style={{ color: '#d1d5db' }}>—</span>}
                  </td>
                  <td style={{ ...tdStyle(index), fontWeight: 500 }}>
                    {formatMoney(payment.credit_balance)}
                  </td>
                  <td style={tdStyle(index)}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
                      borderRadius: '9999px', fontSize: '0.68rem', fontWeight: 600,
                      backgroundColor: st.bg, color: st.color,
                    }}>
                      {payment.status_description}
                    </span>
                  </td>
                  <td style={tdStyle(index)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                      <PDFDownloadLink document={<PDFTicket item={payment} />} fileName={`ticket-${payment.invoice}.pdf`}>
                        {iconBtn(null, "Comprobante", "text-blue-500 fa-solid fa-file-invoice", "#dbeafe")}
                      </PDFDownloadLink>

                      {payment.evidence &&
                        iconBtn(() => viewEvidence(payment), "Evidencia", "text-blue-500 fa-solid fa-file", "#dbeafe")}

                      {showActions && <>
                        {iconBtn(
                          () => applyPayment(payment, 2), "Declinar",
                          parseInt(payment.status) === 2
                            ? "text-red-500 fa-solid fa-circle-xmark"
                            : "text-gray-300 fa-solid fa-circle-xmark",
                          "#fee2e2"
                        )}
                        {iconBtn(
                          () => applyPayment(payment, 0), "Aprobar",
                          parseInt(payment.status) === 0
                            ? "text-green-500 fa-solid fa-check-circle"
                            : "text-gray-300 fa-solid fa-check-circle",
                          "#dcfce7"
                        )}
                      </>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '12px 16px', borderTop: '1px solid #f1f5f9', backgroundColor: '#f8fafc',
      }}>
        <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {indexOfFirstItem + 1}–{Math.min(indexOfLastItem, sortedPayments.length)} de {sortedPayments.length}
        </span>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{
              padding: '4px 12px', fontSize: '0.75rem', borderRadius: '5px', border: '1px solid #e2e8f0',
              backgroundColor: '#fff', color: '#374151', cursor: 'pointer',
              opacity: currentPage === 1 ? 0.4 : 1,
            }}
          >
            ← Anterior
          </button>
          <span style={{ fontSize: '0.75rem', color: '#6b7280', padding: '0 4px' }}>
            {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages || totalPages === 0}
            style={{
              padding: '4px 12px', fontSize: '0.75rem', borderRadius: '5px', border: '1px solid #e2e8f0',
              backgroundColor: '#fff', color: '#374151', cursor: 'pointer',
              opacity: currentPage === totalPages || totalPages === 0 ? 0.4 : 1,
            }}
          >
            Siguiente →
          </button>
        </div>
      </div>

      {/* Modal evidencia */}
      {selectedPayment && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 50,
        }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '10px', width: '100%', maxWidth: '600px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
            }}>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>Comprobante de Pago</h3>
              <button
                onClick={() => setSelectedPayment(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#9ca3af', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px',
                backgroundColor: '#f8fafc', borderRadius: '6px', padding: '12px', marginBottom: '16px', fontSize: '0.8rem',
              }}>
                <div><span style={{ color: '#6b7280' }}>Factura</span><br /><strong>{selectedPayment.invoice}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Monto</span><br /><strong>{formatMoney(selectedPayment.amount)}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Fecha</span><br /><strong>{formatDate(selectedPayment.transfer_date)}</strong></div>
                <div><span style={{ color: '#6b7280' }}>Folio</span><br /><strong>{selectedPayment.folio_transfer}</strong></div>
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', overflow: 'hidden' }}>
                <img
                  src={selectedPayment.evidence_base64}
                  alt="Comprobante de pago"
                  style={{ width: '100%', height: 'auto', maxHeight: '380px', objectFit: 'contain', display: 'block' }}
                  onError={e => e.target.style.display = 'none'}
                />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid #f1f5f9' }}>
              <button
                onClick={() => setSelectedPayment(null)}
                style={{
                  padding: '7px 18px', backgroundColor: '#111827', color: '#fff',
                  border: 'none', borderRadius: '6px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: 600,
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistoryTable;